<template>
  <div class="seller" :class="{ 'seller--nav-open': navOpen }" :style="scaleStyle">
    <!-- ================= 사이드바 ================= -->
    <aside class="seller__side">
      <div class="seller__brand">
        <span class="seller__brand-mark">
          <AppIcon icon="mdi-storefront" :size="22" />
        </span>
        <span class="seller__brand-text">
          <strong>{{ MALL_NAME }}</strong>
          <em>판매관리센터</em>
        </span>
      </div>

      <div class="seller__store">
        <p class="seller__store-name">{{ storeName }}</p>
        <p class="seller__store-meta">
          <span class="seller__grade">{{ gradeLabel }}</span>
          <span>{{ bizName }}</span>
        </p>
      </div>

      <nav class="seller__nav" aria-label="판매관리센터 메뉴">
        <template v-for="group in navGroups" :key="group.title">
          <p class="seller__nav-title">{{ group.title }}</p>
          <router-link
            v-for="item in group.items"
            :key="item.label"
            :to="item.to"
            class="seller__nav-link"
            :class="{ 'is-active': isActive(item) }"
            :data-tour="item.tour"
            @click="navOpen = false"
          >
            <AppIcon :icon="item.icon" :size="19" />
            <span>{{ item.label }}</span>
            <em v-if="item.badge" class="seller__nav-badge">{{ item.badge }}</em>
          </router-link>
        </template>
      </nav>

      <div class="seller__side-foot">
        <a class="seller__side-link" :href="MALL_HOME_PATH" target="_blank" rel="noopener">
          <AppIcon icon="mdi-open-in-new" :size="17" />
          함우리몰 보기
        </a>
        <button type="button" class="seller__side-link" @click="onLogout">
          <AppIcon :icon="isViewingAsAdmin ? 'mdi-arrow-left' : 'mdi-logout-variant'" :size="17" />
          {{ isViewingAsAdmin ? '관리자로 돌아가기' : '로그아웃' }}
        </button>
      </div>
    </aside>

    <!-- ================= 본문 ================= -->
    <div class="seller__body">
      <div
        v-if="isViewingAsAdmin"
        class="seller__impersonate"
        data-tour="seller-viewing-as-admin"
      >
        <AppIcon icon="mdi-shield-account-outline" :size="18" />
        <span>
          최고관리자({{ viewingAdminLabel }})로
          <strong>{{ storeName }}</strong>
          ({{ seller?.sellerId }}) 화면을 보고 있습니다.
        </span>
        <button type="button" class="seller__impersonate-exit" @click="onExitView">
          관리자로 돌아가기
        </button>
      </div>

      <header class="seller__top">
        <button
          type="button"
          class="seller__nav-toggle"
          :aria-expanded="navOpen"
          aria-label="메뉴 열기"
          @click="navOpen = !navOpen"
        >
          <AppIcon :icon="navOpen ? 'mdi-close' : 'mdi-menu'" :size="22" />
        </button>

        <div class="seller__top-title" data-tour="page-title">
          <h1>{{ pageTitle }}</h1>
          <p v-if="pageDesc">{{ pageDesc }}</p>
        </div>

        <div class="seller__top-right">
          <FeatureSpecPanel />
          <span class="seller__today">{{ today }}</span>

          <div class="seller__alerts" ref="alertRef" data-tour="seller-notify-panel">
            <button
              type="button"
              class="seller__icon-btn"
              :aria-expanded="alertOpen"
              aria-label="알림"
              @click="onToggleAlerts"
            >
              <AppIcon icon="mdi-bell-outline" :size="20" />
              <em v-if="bellCount" class="seller__alert-dot">{{ bellCount }}</em>
            </button>

            <transition name="seller-pop">
              <div v-if="alertOpen" class="seller__alert-panel">
                <p class="seller__alert-head">처리가 필요한 일</p>
                <router-link
                  v-for="alert in alerts"
                  :key="alert.label"
                  :to="alert.to"
                  class="seller__alert-item"
                  @click="alertOpen = false"
                >
                  <AppIcon :icon="alert.icon" :size="18" />
                  <span>{{ alert.label }}</span>
                  <strong>{{ alert.count }}</strong>
                </router-link>
                <p v-if="!alerts.length" class="seller__alert-empty">지금은 처리할 일이 없습니다.</p>

                <p class="seller__alert-head seller__alert-head--sub">최근 알림</p>
                <button
                  v-for="item in recentNotifies"
                  :key="item.idx"
                  type="button"
                  class="seller__notify-item"
                  :class="{ 'is-unread': !item.read }"
                  @click="openNotify(item)"
                >
                  <strong>{{ item.title }}</strong>
                  <em v-if="item.orderCode" class="seller__notify-order">{{ item.orderCode }}</em>
                  <span v-if="item.body">{{ item.body }}</span>
                </button>
                <p v-if="!recentNotifies.length" class="seller__alert-empty">새 알림이 없습니다.</p>
              </div>
            </transition>
          </div>

          <!-- 계정 주체는 사람이 아니라 업체다. 대표자명 대신 업체명을 세운다. -->
          <div class="seller__user">
            <span class="seller__avatar">{{ storeInitial }}</span>
            <span class="seller__user-text">
              <strong>{{ storeName }}</strong>
              <em>{{ statusLabel }}</em>
            </span>
          </div>
        </div>
      </header>

      <FeatureTourOverlay />

      <main class="seller__main">
        <!-- 심사 중에는 둘러보기만 되고 상품 등록·판매는 열리지 않는다 -->
        <div v-if="isPending && !mustOnboard" class="seller__pending">
          <AppIcon icon="mdi-clock-outline" :size="20" />
          <span>
            <strong>승인 심사가 진행 중입니다.</strong>
            온보딩·사업자 정보를 확인하는 동안 화면을 미리 둘러볼 수 있습니다.
            관리자 승인이 완료되면 상품 등록과 판매가 열립니다.
          </span>
        </div>
        <slot />
      </main>
    </div>

    <div v-if="navOpen" class="seller__scrim" @click="navOpen = false" />

    <transition name="sui-toast">
      <p v-if="toast" class="sui-toast">
        <AppIcon icon="mdi-check-circle" :size="18" />
        {{ toast }}
      </p>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import FeatureSpecPanel from '@/components/manage/FeatureSpecPanel.vue'
import FeatureTourOverlay from '@/components/manage/FeatureTourOverlay.vue'
import { MALL_HOME_PATH, MALL_NAME } from '@/constants/mall'
import { SELLER_GRADE_LABEL, SELLER_STATUS_LABEL } from '@/constants/sellerSession'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useManageUiScale } from '@/composables/useManageUiScale'
import { useSellerData } from '@/composables/useSellerData'
import { fetchSellerOrders } from '@/api/mallOrder'
import { fetchSellerQnas } from '@/api/mallReview'
import {
  fetchSellerNotifies,
  markSellerNotifiesRead,
  type SellerNotifyItem,
} from '@/api/seller'

const route = useRoute()
const router = useRouter()
const { seller, isPending, isViewingAsAdmin, logout } = useSellerAuth()
const { scaleStyle } = useManageUiScale()
/* 주문 화면 등에서 올리는 토스트는 레이아웃이 받아서 보여 준다 */
const { toast } = useSellerData()

const mustOnboard = computed(() => Boolean(seller.value?.mustOnboard))
const viewingAdminLabel = computed(() => {
  const view = seller.value?.viewingAsAdmin
  if (!view) {
    return ''
  }
  return view.adminName || view.adminId
})
const storeName = computed(() => seller.value?.storeName ?? '판매업체')
const bizName = computed(() => seller.value?.bizName ?? '')
const storeInitial = computed(() => storeName.value.slice(0, 1) || '몰')
const gradeLabel = computed(() => SELLER_GRADE_LABEL[seller.value?.grade ?? 'NORMAL'] ?? '일반')
const statusLabel = computed(() =>
  seller.value ? SELLER_STATUS_LABEL[seller.value.status] : '확인 중',
)

const navOpen = ref(false)
const alertOpen = ref(false)
const alertRef = ref<HTMLElement | null>(null)

const newOrders = ref(0)
const readyOrders = ref(0)
const claimOrders = ref(0)
const openInquiries = ref(0)
const recentNotifies = ref<SellerNotifyItem[]>([])
const unreadNotifies = ref(0)

async function loadBadges() {
  if (mustOnboard.value) {
    newOrders.value = 0
    readyOrders.value = 0
    claimOrders.value = 0
    openInquiries.value = 0
    recentNotifies.value = []
    unreadNotifies.value = 0
    return
  }
  try {
    const [orders, qnas, notifies] = await Promise.all([
      fetchSellerOrders().catch(() => null),
      fetchSellerQnas().catch(() => []),
      fetchSellerNotifies().catch(() => null),
    ])
    newOrders.value = orders?.counts.PAID ?? 0
    readyOrders.value = orders?.counts.READY ?? 0
    claimOrders.value =
      (orders?.counts.CANCEL_REQUEST ?? 0) + (orders?.counts.RETURN_REQUEST ?? 0)
    openInquiries.value = qnas.filter((item) => !item.answer).length
    recentNotifies.value = (notifies?.items || []).slice(0, 8)
    unreadNotifies.value = notifies?.unread ?? 0
  } catch {
    /* 배지는 실패해도 화면은 살아 있어야 한다 */
  }
}

function notifyLink(item: SellerNotifyItem): string {
  const code = String(item.orderCode || '').trim()
  if (item.type === 'CLAIM' || item.type === 'SELLER_CLAIM') {
    return code
      ? `/seller/claims?keyword=${encodeURIComponent(code)}`
      : '/seller/claims'
  }
  if (item.type === 'SELLER_INQUIRY' || item.type === 'INQUIRY') {
    return code
      ? `/seller/reviews?tab=inquiry&filter=open&keyword=${encodeURIComponent(code)}`
      : '/seller/reviews?tab=inquiry&filter=open'
  }
  if (code) {
    return `/seller/orders?keyword=${encodeURIComponent(code)}`
  }
  return '/seller/orders'
}

async function openNotify(item: SellerNotifyItem) {
  alertOpen.value = false
  try {
    if (!item.read) {
      unreadNotifies.value = await markSellerNotifiesRead(item.idx)
      item.read = true
    }
  } catch {
    /* 이동은 계속 */
  }
  router.push(notifyLink(item))
}

async function onToggleAlerts() {
  alertOpen.value = !alertOpen.value
  if (!alertOpen.value || !unreadNotifies.value) return
  try {
    unreadNotifies.value = await markSellerNotifiesRead()
    recentNotifies.value = recentNotifies.value.map((row) => ({ ...row, read: true }))
  } catch {
    /* 읽음 실패해도 목록은 유지 */
  }
}



interface NavItem {
  label: string
  to: string
  icon: string
  badge?: number
  exact?: boolean
  tour?: string
}

const navGroups = computed<Array<{ title: string; items: NavItem[] }>>(() => {
  if (mustOnboard.value) {
    return [
      {
        title: '입점 준비',
        items: [
          {
            label: '온보딩',
            to: '/seller/onboarding',
            icon: 'mdi-clipboard-check-outline',
            exact: true,
          },
        ],
      },
    ]
  }
  return [
  {
    title: '판매 현황',
    items: [{ label: '대시보드', to: '/seller', icon: 'mdi-view-dashboard-outline', exact: true }],
  },
  {
    title: '주문 · 배송',
    items: [
      {
        label: '주문 관리',
        to: '/seller/orders',
        icon: 'mdi-clipboard-list-outline',
        badge: newOrders.value + readyOrders.value,
      },
      { label: '취소 · 반품', to: '/seller/claims', icon: 'mdi-package-variant-remove', badge: claimOrders.value },
    ],
  },
  {
    title: '상품',
    items: [
      {
        label: '상품 관리',
        to: '/seller/products',
        icon: 'mdi-package-variant-closed',
        tour: 'seller-products-nav',
      },
    ],
  },
  {
    title: '고객 · 정산',
    items: [
      { label: '리뷰 · 문의', to: '/seller/reviews', icon: 'mdi-comment-text-outline', badge: openInquiries.value },
      { label: '정산 관리', to: '/seller/settlements', icon: 'mdi-cash-multiple' },
    ],
  },
  {
    title: '설정',
    items: [{ label: '판매업체 정보', to: '/seller/settings', icon: 'mdi-cog-outline' }],
  },
]
})

const alerts = computed(() =>
  [
    { label: '신규 주문 확인', count: newOrders.value, to: '/seller/orders?status=PAID', icon: 'mdi-cart-arrow-down' },
    { label: '발송 대기', count: readyOrders.value, to: '/seller/orders?status=READY', icon: 'mdi-truck-outline' },
    { label: '취소 · 반품 요청', count: claimOrders.value, to: '/seller/claims', icon: 'mdi-alert-outline' },
    { label: '미답변 문의', count: openInquiries.value, to: '/seller/reviews?tab=inquiry&filter=open', icon: 'mdi-help-circle-outline' },
  ].filter((alert) => alert.count > 0),
)

const bellCount = computed(() => alerts.value.length + unreadNotifies.value)

const pageTitle = computed(() => (route.meta.sellerTitle as string) ?? '판매관리센터')
const pageDesc = computed(() => (route.meta.sellerDesc as string) ?? '')

const today = new Date().toLocaleDateString('ko-KR', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  weekday: 'short',
})

function isActive(item: NavItem): boolean {
  const [itemPath, itemQuery = ''] = item.to.split('?')

  if (itemQuery) {
    if (route.path !== itemPath) return false
    const want = new URLSearchParams(itemQuery)
    return [...want.entries()].every(([key, value]) => String(route.query[key] ?? '') === value)
  }

  if (item.exact) return route.path === item.to

  if (route.path === itemPath) return true
  if (!route.path.startsWith(`${itemPath}/`)) return false

  const rivals = navGroups.value
    .flatMap((group) => group.items)
    .filter((other) => {
      const otherPath = other.to.split('?')[0]
      return other.to !== item.to && otherPath.startsWith(`${itemPath}/`)
    })
  if (rivals.some((other) => {
    const otherPath = other.to.split('?')[0]
    return route.path === otherPath || route.path.startsWith(`${otherPath}/`)
  })) {
    return false
  }
  return true
}

async function onExitView() {
  await logout()
  await router.push({ name: 'admin-seller-view' })
}

async function onLogout() {
  if (isViewingAsAdmin.value) {
    if (!window.confirm('판매업체 보기를 끝내고 관리자 화면으로 돌아갈까요?')) {
      return
    }
    await onExitView()
    return
  }

  if (!window.confirm('판매관리센터에서 로그아웃할까요?')) {
    return
  }

  await logout()

  /* 새 창으로 연 경우엔 그대로 닫고, 직접 주소로 들어왔다면 로그인 화면으로 돌려보낸다. */
  window.close()
  window.setTimeout(() => {
    if (!window.closed) {
      router.push({ path: '/login', query: { tab: 'seller' } })
    }
  }, 120)
}

function onDocClick(event: MouseEvent) {
  if (!alertOpen.value) return
  if (alertRef.value?.contains(event.target as Node)) return
  alertOpen.value = false
}

watch(
  () => route.fullPath,
  () => {
    navOpen.value = false
    alertOpen.value = false
    loadBadges()
  },
)

onMounted(() => {
  loadBadges()
  document.addEventListener('click', onDocClick)
})
onUnmounted(() => document.removeEventListener('click', onDocClick))
</script>

<style lang="scss" src="./SellerLayout.scss"></style>
